import { Component, DestroyRef, inject, Input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NextFormComponent } from '@app/next/declarative-forms/components/declarative-form/next-form.component';
import { ModalComponent } from '@app/next/messaging/components/modal/modal.component';
import { FormActionEvent } from '@app/verify/feature/forms/components/form-page/form-page.component';
import { logger } from '@fmnts/common/log';
import { Modal } from '@fmnts/components/modal';
import { ValidationStrategy } from '@fmnts/declarative-forms';
import {
  DonorDeviceSignalStore,
  SelfCheckoutStreamStore,
} from '@formunauts/self-checkout/data-access';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import { prepareFormData } from '@formunauts/shared/campaigning/data-access';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';
import { LegacyFmntsApiError } from '@formunauts/shared/errors/domain';
import { faHandHoldingSeedling } from '@fortawesome/pro-solid-svg-icons';
import * as Sentry from '@sentry/angular';
import * as O from 'effect/Option';
import { catchError, EMPTY, filter, map } from 'rxjs';
import {
  ClientOfflineModalComponent,
  ClientTimeoutModalComponent,
  CookiesDisabledModalComponent,
} from '../components/form-modals/self-checkout-modals.component';
import * as Utils from '../self-checkout-feature-utils';
enum ActionKeys {
  Next = 'nextPage',
}

@Component({
  selector: 'verify-auth-for-self-checkout-form',
  templateUrl: './auth-for-self-checkout-form.component.html',
})
export class AuthForSelfCheckoutFormComponent {
  protected readonly donorDeviceStore = inject(DonorDeviceSignalStore);
  protected readonly selfCheckoutStore = inject(SelfCheckoutStreamStore);
  protected readonly router = inject(Router);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly modal = inject(Modal);
  protected readonly destroyRef = inject(DestroyRef);

  public authenticateForm = signal<SelfCheckout.AuthenticationForm>(null!);
  public hash = signal<SelfCheckout.Hash>(this.selfCheckoutStore.hash());
  public disabledActions: string[] = [];
  private readonly _log = logger({
    name: AuthForSelfCheckoutFormComponent.name,
  });

  public faIcon = faHandHoldingSeedling;

  @Input() validationStrategy = ValidationStrategy.OnChange;

  constructor() {
    toObservable(this.selfCheckoutStore.selfCheckoutForm)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((value) => {
        this.router.navigate(['confirm'], {
          relativeTo: this.activatedRoute,
        });
      });
    toObservable(this.selfCheckoutStore.authForm)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((authForm) => {
        this.authenticateForm.set(authForm.value);
        this.donorDeviceStore.setLanguages(authForm.value.language);
        this.donorDeviceStore.setLocale(authForm.value.locale);
      });

    toObservable(this.selfCheckoutStore.authFormError)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((error) =>
        this.showErrorModal(this.authenticateForm(), error.value),
      );

    toObservable(this.selfCheckoutStore.selfCheckoutFormError)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((error) =>
        this.showErrorModal(this.authenticateForm(), error.value),
      );
  }

  handleIsValidChanged(isValid: boolean) {
    // Disable next
    this.disabledActions = isValid ? [] : [ActionKeys.Next];
  }

  handleAction(formActionEvent: FormActionEvent) {
    const { action, formComponent } = formActionEvent;
    switch (action) {
      case ActionKeys.Next:
        return this.submitForm(formComponent);

      default:
        this._log.error(`Unknown action: ${action}`);
    }
  }

  private submitForm(formComponent: NextFormComponent) {
    formComponent
      .submit$()
      .pipe(
        catchError(() => {
          this._log.info('Invalid form');
          return EMPTY;
        }),
        map((formData) =>
          prepareFormData(
            formData,
            CampaignForm.getFieldsOnPage(this.authenticateForm().form),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((preparedData) => {
        this.selfCheckoutStore.authenticate({
          preparedData: SelfCheckout.AuthenticationData(preparedData),
          hash: this.hash(),
        });
      });
  }

  private showErrorModal(
    authForm: SelfCheckout.AuthenticationForm,
    error: SelfCheckout.SelfCheckoutError,
  ) {
    switch (error._tag) {
      case 'ClientTimeoutError':
        return this.modal.open(ClientTimeoutModalComponent);
      case 'ClientOfflineError':
        return this.modal.open(ClientOfflineModalComponent);
      case 'SelfCheckoutCSRFError': {
        Sentry.captureException(
          new LegacyFmntsApiError({
            httpError: {
              ...error.httpError,
              message: error.httpError.error.message,
            },
          }),
        );
        return this.modal.open(CookiesDisabledModalComponent);
      }
      default: {
        const modal = Utils.findHttpModalOrGenericFallback(
          authForm.form.modals,
          error instanceof SelfCheckout.SelfCheckoutCustomError
            ? (error.status ?? null)
            : null,
        );
        return this.modal.open(ModalComponent, {
          minWidth: '25vw',
          data: { modal: { ...modal } },
        });
      }
    }
  }
}
